import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PickersDay } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import UserHint from "../../../../components/display/UserHint";
import { mandatory, useIsMobile } from "../../../../utils/Functions";
export const labels = {
  numberOfDays: "numberOfDays",
  dosageMorning: "morningDoses",
  dosageMorningCheckbox: "morningDosesCheckbox",
  dosageEvening: "eveningDoses",
  dosageEveningCheckbox: "eveningDosesCheckbox",
  alert: "alert",
  dates: "dates",
};

export default function TherapyDetails() {
  const methods = useFormContext();
  const { t } = useTranslation();

  const selectedDates = methods.watch(labels.dates) || [];
  const numberOfDays = methods.watch(labels.numberOfDays);

  const handleDateChange = (newDate) => {
    if (!newDate) return;

    const formatted = dayjs(newDate).format("DD/MM/YYYY");
    const currentDates = methods.getValues(labels.dates) || [];

    const updated = currentDates.includes(formatted)
      ? currentDates.filter((d) => d !== formatted)
      : [...currentDates, formatted];

    methods.setValue(labels.dates, updated, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const datesToShowAsSelected = numberOfDays
    ? Array.from({ length: numberOfDays }, (_, i) =>
        dayjs().add(i, "day").format("DD/MM/YYYY"),
      )
    : selectedDates;

  useEffect(() => {
    if (numberOfDays) {
      methods.setValue(labels.dates, [], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [numberOfDays]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%" }}>
        <Grid container spacing={3}>
          {/* Left Panel - Form Controls */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              {/* Therapy Configuration Card */}
              <Card variant="elevation" elevation={1}>
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    {t("TherapyConfiguration")}
                  </Typography>

                  <Stack spacing={2.5}>
                    {/* Duration Selection */}
                    <FormControl fullWidth>
                      <InputLabel id="duration-label">
                        {t("DurationDays")}
                      </InputLabel>

                      <Controller
                        name={labels.numberOfDays}
                        control={methods.control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            labelId="duration-label"
                            label={mandatory(t("Duration (Days)"))}
                            value={field.value ?? ""}
                            MenuProps={{
                              PaperProps: { sx: { maxHeight: 300 } },
                            }}
                          >
                            <MenuItem value="">{t("PickCalendar")}</MenuItem>

                            {getNumberOfDaysArray().map((number) => (
                              <MenuItem key={number} value={number}>
                                {number}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>

                    {/* Dosage Section */}
                    <DosagesSelector />

                    {/* Alert Section */}
                    <Paper
                      variant="outlined"
                      sx={{ p: 2, bgcolor: "action.hover" }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <NotificationsIcon color="primary" />
                        <Box sx={{ flexGrow: 1 }}>
                          <Controller
                            name={labels.alert}
                            control={methods.control}
                            render={({ field }) => (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    {...field}
                                    checked={!!field.value}
                                  />
                                }
                                label={
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight={500}
                                    >
                                      {t("MakeAlert")}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {t("MakeAlertInfo")}
                                    </Typography>
                                  </Box>
                                }
                              />
                            )}
                          />
                        </Box>
                      </Stack>
                    </Paper>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* Right Panel - Calendar */}
          <Grid item xs={12} md={6}>
            <Card variant="elevation" elevation={1} sx={{ height: "100%" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 2 }}
                >
                  {t("SelectTreatmentDates")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <DateCalendar
                    disablePast
                    onChange={handleDateChange}
                    slots={{
                      day: (props) => {
                        const isSelected = datesToShowAsSelected.some((date) =>
                          dayjs(date, "DD/MM/YYYY").isSame(props.day, "day"),
                        );

                        return (
                          <PickersDay
                            {...props}
                            selected={isSelected}
                            disabled={!!numberOfDays}
                          />
                        );
                      },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
}

function getNumberOfDaysArray() {
  const array = [];
  for (var i = 0; i < 31; i++) {
    array.push(i + 1);
  }
  return array;
}

export function DosagesSelector() {
  const { t } = useTranslation();
  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {`${t("DailyDosage")} (${t("CheckWantedDosage")})`}
      </Typography>
      <Stack spacing={2}>
        <NumberInputField
          label="Morning"
          helperText="e.g. 15 mg"
          registerTitle={labels.dosageMorning}
        />
        <NumberInputField
          label="Evening"
          helperText="e.g. 25 mg"
          registerTitle={labels.dosageEvening}
        />
      </Stack>
    </Box>
  );
}

export function NumberInputField({
  registerTitle = "",
  label = "",
  placeholder = "",
  helperText = "",
}) {
  const methods = useFormContext();
  const { t } = useTranslation();

  const checkboxField = `${registerTitle}Checkbox`;

  const enabled = methods.watch(checkboxField);

  const handleInput = (e) => {
    let value = e.target.value;
    e.target.value = value;
  };

  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"flex-start"}
    >
      <Box sx={{ height: 56, display: "flex", alignItems: "center" }}>
        <Checkbox
          checked={!!enabled}
          onChange={(e) => {
            const isChecked = e.target.checked;
            methods.setValue(checkboxField, isChecked, { shouldDirty: true });

            if (!isChecked) {
              methods.setValue(registerTitle, "", { shouldDirty: true });
              methods.clearErrors(registerTitle);
            }
          }}
        />
      </Box>
      <Controller
        name={registerTitle}
        control={methods.control}
        rules={{
          validate: (val) => {
            if (enabled && !val) return t("CantBeEmpty");
            return true;
          },
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            fullWidth
            label={t(label)}
            placeholder={placeholder}
            disabled={!enabled}
            required={!!enabled}
            onInput={handleInput}
            error={!!fieldState.error}
            helperText={fieldState.error?.message ?? helperText}
            variant="outlined"
            inputProps={{ maxLength: 120 }}
            InputLabelProps={{
              shrink: !!enabled && !!field.value,
            }}
          />
        )}
      />
      <Box sx={{ height: 56, display: "flex", alignItems: "center" }}>
        <UserHint iconOnly hint={"DosageHint"} sx={{ mb: 0.3 }} />
      </Box>
    </Stack>
  );
}

export function NumberInputFieldEditing({
  registerTitle = "",
  label = "",
  placeholder = "",
  helperText = "",
  checkboxFieldRegister = "",
}) {
  const isMobile = useIsMobile();
  const methods = useFormContext();
  const { t } = useTranslation();

  const enabled = methods.watch(checkboxFieldRegister);

  const handleInput = (e) => {
    let value = e.target.value;
    e.target.value = value;
  };

  return (
    <Stack
      direction={"row"}
      justifyContent={"space-between"}
      alignItems={"flex-start"}
    >
      <Box sx={{ height: 56, display: "flex", alignItems: "center" }}>
        <Checkbox
          checked={!!enabled}
          onChange={(e) => {
            const isChecked = e.target.checked;
            methods.setValue(checkboxFieldRegister, isChecked, {
              shouldDirty: true,
            });

            if (!isChecked) {
              methods.setValue(registerTitle, "", { shouldDirty: true });
              methods.clearErrors(registerTitle);
            }
          }}
        />
      </Box>

      <Controller
        name={registerTitle}
        control={methods.control}
        rules={{
          validate: (val) => {
            if (enabled && !val) return t("CantBeEmpty");
            return true;
          },
        }}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            fullWidth
            label={t(label)}
            inputProps={{ maxLength: 120 }}
            placeholder={placeholder}
            disabled={!enabled}
            onInput={handleInput}
            required={!!enabled}
            error={!!fieldState.error}
            helperText={fieldState.error?.message ?? helperText}
            variant="outlined"
            InputLabelProps={{
              shrink: !!enabled && !!field.value,
            }}
          />
        )}
      />
      <Box sx={{ height: 56, display: "flex", alignItems: "center" }}>
        <UserHint
          iconOnly
          hint={"DosageHint"}
          maxWidth={isMobile ? "100%" : "80%"}
          sx={{ mb: 0.3 }}
        />
      </Box>
    </Stack>
  );
}
